/**
 * The address ledger: every mailbox a campaign has ever enrolled.
 *
 * ⚠ THE LIST IS NOT "PEOPLE WE HAVE WRITTEN TO" AND THE HEADS SAY SO. A row
 * appears the moment enrolment writes it down, which sends nothing; the
 * "letters" column is what says a message actually left. Both numbers are on
 * every row for that reason, and the sub-line under the address names the state
 * in words rather than leaving it to be inferred from a zero.
 *
 * There is no "shared: yes" filter here on purpose. `MktContact.shared` is
 * rewritten on every enrolment against THAT campaign's threshold, so it reports
 * a campaign setting under the name of a fact about the address. The filter is
 * "at least N companies" instead, which is a number with one meaning - and the
 * live question belongs to the shared tab, which asks the register.
 */
import { useCallback, useEffect, useState } from "react";
import { AtSign, ChevronLeft, ChevronRight, Loader2, Users } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  listContacts,
  type ContactPersonType,
  type ContactRow,
  type ContactStatus,
} from "../../../../lib/marketingContacts";
import {
  Btn, EmptyState, MenuField, SearchField, SortHead, StatusPill, Toolbar, ToolbarGap,
  fmtNum, fmtStamp, listCard,
} from "../../../ui";
import { Note, num } from "../shared";
import { contactTone } from "../status";
import { nextSort, rowState, showingRange, type SortState } from "./lib";

const ALL = "__all__";

/** Address, standing, companies, letters, last contact. */
const COLS = "minmax(0,2.3fr) 1fr .85fr .85fr 1.2fr";

const PAGE_SIZE = 50;

const STATUSES: ContactStatus[] = ["active", "unsubscribed", "bounced", "complained", "blocked"];
const PERSON_TYPES: ContactPersonType[] = ["legal", "natural", "unknown"];
/** The thresholds worth offering. 5 is the sender's own default. */
const COMPANY_STEPS = [2, 5, 20];

export function LedgerTab({ onOpen }: { onOpen: (address: string) => void }) {
  const t = useT();

  const [rows, setRows] = useState<ContactRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [status, setStatus] = useState<string>(ALL);
  const [personType, setPersonType] = useState<string>(ALL);
  const [contacted, setContacted] = useState<string>(ALL);
  const [mailbox, setMailbox] = useState<string>(ALL);
  const [minCompanies, setMinCompanies] = useState<string>(ALL);
  const [sort, setSort] = useState<SortState>({ sort: "lastContacted", order: "desc" });

  useEffect(() => {
    const id = setTimeout(() => setCommittedQ(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card, which
  // reads as "the filter found nothing".
  useEffect(() => {
    setPage(1);
  }, [committedQ, status, personType, contacted, mailbox, minCompanies, sort]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await listContacts({
        ...(committedQ ? { q: committedQ } : {}),
        ...(status === ALL ? {} : { status: status as ContactStatus }),
        ...(personType === ALL ? {} : { personType: personType as ContactPersonType }),
        ...(contacted === ALL ? {} : { contacted: contacted === "yes" ? "1" : "0" }),
        ...(mailbox === ALL ? {} : { freemail: mailbox === "free" ? "1" : "0" }),
        ...(minCompanies === ALL ? {} : { minCompanies: Number(minCompanies) }),
        sort: sort.sort,
        order: sort.order,
        page,
        pageSize: PAGE_SIZE,
      });
      setRows(res.rows);
      setTotal(res.total);
      setPageCount(Math.max(1, res.pageCount));
      setError(null);
    } catch {
      setRows([]);
      setError(t("marketing.error.load"));
    }
  }, [committedQ, status, personType, contacted, mailbox, minCompanies, sort, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered =
    committedQ !== "" || status !== ALL || personType !== ALL || contacted !== ALL || mailbox !== ALL || minCompanies !== ALL;
  const range = showingRange(page, PAGE_SIZE, rows?.length ?? 0);

  return (
    <>
      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("marketing.contacts.ledger.searchPlaceholder")} />
        <MenuField
          value={status}
          onChange={setStatus}
          label={t("marketing.contacts.ledger.col.standing")}
          options={[
            { value: ALL, label: t("marketing.contacts.filter.anyStatus") },
            ...STATUSES.map((s) => ({ value: s as string, label: t(`marketing.contacts.status.${s}`) })),
          ]}
        />
        <MenuField
          value={contacted}
          onChange={setContacted}
          label={t("marketing.contacts.filter.contact")}
          options={[
            { value: ALL, label: t("marketing.contacts.filter.anyContact") },
            { value: "yes", label: t("marketing.contacts.filter.contacted") },
            { value: "no", label: t("marketing.contacts.filter.notContacted") },
          ]}
        />
        <MenuField
          value={mailbox}
          onChange={setMailbox}
          label={t("marketing.contacts.filter.mailbox")}
          options={[
            { value: ALL, label: t("marketing.contacts.filter.anyMailbox") },
            { value: "free", label: t("marketing.contacts.filter.freemail") },
            { value: "own", label: t("marketing.contacts.filter.ownDomain") },
          ]}
        />
        <MenuField
          value={personType}
          onChange={setPersonType}
          label={t("marketing.contacts.filter.person")}
          options={[
            { value: ALL, label: t("marketing.contacts.filter.anyPerson") },
            ...PERSON_TYPES.map((p) => ({ value: p as string, label: t(`marketing.contacts.person.${p}`) })),
          ]}
        />
        <MenuField
          value={minCompanies}
          onChange={setMinCompanies}
          label={t("marketing.contacts.ledger.col.companies")}
          options={[
            { value: ALL, label: t("marketing.contacts.filter.anyCompanies") },
            ...COMPANY_STEPS.map((n) => ({
              value: String(n),
              label: t("marketing.contacts.filter.minCompanies", { min: n }),
            })),
          ]}
        />
        <ToolbarGap />
        <span className="crm-sub px-1.5">
          {rows ? t("marketing.contacts.ledger.count", { count: fmtNum(total) }) : t("common.loading")}
        </span>
      </Toolbar>

      {error ? (
        <Note tone="bad" className="mb-3.5">
          {error}
        </Note>
      ) : null}

      {/* Deliberately NOT `crm-list-stack`. That modifier folds a row into
          a block below 560px and hides the column heads with it, which is
          right for a row of words and wrong for this one: two of its five
          cells are bare integers, and "2" above "0" with no captions says
          nothing. The card scrolls sideways instead, keeping the heads.
          index.css:2286 makes the same argument about document lines. */}
      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          // An empty ledger and a filter that matched nothing are different
          // facts, and the one that says "no campaign has enrolled anybody"
          // while six filters are on sends somebody looking for a bug.
          <EmptyState
            icon={AtSign}
            title={t(filtered ? "marketing.contacts.ledger.noMatch" : "marketing.contacts.ledger.empty")}
            body={t(filtered ? "marketing.contacts.ledger.noMatchBody" : "marketing.contacts.ledger.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <SortHead
                label={t("marketing.contacts.ledger.col.address")}
                column="address"
                sort={sort.sort}
                order={sort.order}
                onSort={(c) => setSort(nextSort(sort, c))}
              />
              <span>{t("marketing.contacts.ledger.col.standing")}</span>
              <SortHead
                label={t("marketing.contacts.ledger.col.companies")}
                column="companies"
                sort={sort.sort}
                order={sort.order}
                onSort={(c) => setSort(nextSort(sort, c))}
              />
              <SortHead
                label={t("marketing.contacts.ledger.col.letters")}
                column="contacts"
                sort={sort.sort}
                order={sort.order}
                onSort={(c) => setSort(nextSort(sort, c))}
              />
              <SortHead
                label={t("marketing.contacts.ledger.col.last")}
                column="lastContacted"
                sort={sort.sort}
                order={sort.order}
                onSort={(c) => setSort(nextSort(sort, c))}
              />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((r) => {
                const state = rowState(r);
                return (
                  <div key={r.emailKey} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="crm-av crm-av-soft">
                        {r.companyCount > 1 ? <Users className="size-4" /> : <AtSign className="size-4" />}
                      </span>
                      <span className="min-w-0">
                        {/* A button rather than a link: the address opens a
                            panel, not a page. The ::after stretches it over the
                            whole row so the row is clickable end to end. */}
                        <button
                          type="button"
                          onClick={() => onOpen(r.email)}
                          className="crm-prow-n crm-trunc block text-left after:absolute after:inset-0 after:rounded-full"
                        >
                          {r.email}
                        </button>
                        <span className="crm-prow-s crm-trunc block">
                          {t(`marketing.contacts.ledger.state.${state}`)}
                        </span>
                      </span>
                    </span>

                    <span>
                      <StatusPill tone={contactTone(r.status)}>
                        {t(`marketing.contacts.status.${r.status}`)}
                      </StatusPill>
                    </span>

                    <span className="crm-tnum text-[13px]">{num(r.companyCount)}</span>
                    <span className="crm-tnum text-[13px]">{num(r.contactCount)}</span>
                    {/* `fmtStamp` returns an empty string for null, and an empty
                        cell here would read as a date that failed to render. */}
                    <span className="crm-mut crm-tnum text-[12.5px]">
                      {r.lastContactedAt ? fmtStamp(r.lastContactedAt) : "-"}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="crm-tfoot">
              {t("marketing.contacts.showing", { from: range.from, to: range.to, total })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
